import React, { memo, ReactElement, useContext, useEffect, useState } from 'react';
import { useMutation } from '@apollo/client';
import styled from 'styled-components';
import { Button, InfoButton, WarningButton } from '../connectedForm/Button';
import { ModalBottomButtonContainer } from './Styles';
import RaiseStockAdjustmentFromSalesCredit from '../../graphql/mutations/product/RaiseStockAdjustmentFromSalesCredit.graphql';
import normalizeVariables from '../../graphql/schema/normalizeVariables';
import SalesCreditPostWithAdjustmentMutation from '../../graphql/mutations/salesLedger/SalesCreditPostWithAdjustmentMutation.graphql';
import ConnectedFormContext from '../Context/ConnectedFormContext';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router';
import getClientMutationId from '../../utils/getClientMutationId';
import { alertAction, useComponentQueryErrorHandling } from '../../utils/parseErrorResponse';
import Notification from '../Notification';
import { SalesRefund } from '../../graphql/entities';
import ModalContext from '../Context/ModalContext';
import { usePostConfirmationPrompt } from '../../utils/SalesLedgerHooks';
import {
  AlertType,
  IMetaAction,
  IRaiseStockAdjustmentFromSalesCreditMutationResult,
  IRaiseStockAdjustmentFromSalesCreditMutationVariables,
  ISalesCredit,
  ISalesCreditPostMutationResult,
  ISalesCreditPostMutationVariables,
  ISalesCreditPostWithAdjustmentMutationResult,
  ISalesCreditPostWithAdjustmentMutationVariables,
  ISalesCreditPostWithAdjustmentPayload,
  IStockAdjustment,
  Maybe,
} from '../../graphql/schema/p4types';
import { useP4ToastController } from '../../views/system/NotificationsArea';
import { BasicHeader } from '../../actions/BasicHeader';
import SalesCreditPostMutation from '../../graphql/mutations/salesLedger/SalesCreditPostMutation.graphql';
import FluentModal from './FluentModal';

const LazyProductsStockAdjustmentForm = React.lazy(() => import('../../views/product/ProductsStockAdjustmentForm'));

const Wrapper = styled.div`
  padding: 1em;
`;

const NotificationRefund = ({ salesCredit, onClose }: { salesCredit: ISalesCredit; onClose: () => void }) => {
  const history = useHistory();

  return (
    <Notification
      type={AlertType.Info}
      title={'Refund generation'}
      actionButtons={[
        <InfoButton
          key={'Refund results option'}
          onClick={() => {
            const predefinedFilters = [
              {
                filterOperation: 'EQUALS',
                filterParameters: {
                  field: 'salesCredit.id',
                  value: salesCredit.id,
                },
              },
            ];
            history.replace(SalesRefund.workspaceUrl, { predefinedFilters: predefinedFilters });
            onClose();
          }}
        >
          View refunds?
        </InfoButton>,
      ]}
    />
  );
};

/**
 * @param afterSubmit - Optional 'afterSubmit' argument - If not provided, will call connectedForm.afterSubmit() directly.
 * @param viaTradeCounter - triggered from trade counter
 */
const useSalesCreditPostWithStockAdjustment = (
  { afterSubmit, viaTradeCounter }: { afterSubmit?: (response?: any) => void; viaTradeCounter: boolean } = {
    viaTradeCounter: false,
  }
): [(values: IStockAdjustment) => void, ReactElement, boolean] => {
  const [postConfirmationState, setPostConfirmationState] =
    useState<Maybe<{ meta: any; title: string; message: string }>>(null);
  const [postInProgress, setPostInProgress] = useState(false);

  const dispatch = useDispatch();
  const connectedForm = useContext(ConnectedFormContext);
  const salesCredit = connectedForm.values;

  const handleQueryError = useComponentQueryErrorHandling({
    customAlertHandler: (_: unknown, meta: any, title: string, message: string) => {
      setPostInProgress(false);
      if (meta.confirmationReference) {
        setPostConfirmationState({
          meta,
          title,
          message,
        });
        return;
      }
      //If no custom handling, call the default handling.
      alertAction({ dispatch, entity: connectedForm?.entity }, meta, title, message);
    },
  });
  const [salesCreditPostWithAdjustment] = useMutation<
    ISalesCreditPostWithAdjustmentMutationResult,
    ISalesCreditPostWithAdjustmentMutationVariables
  >(SalesCreditPostWithAdjustmentMutation, {
    onError: handleQueryError,
    onCompleted: (data) => {
      (afterSubmit || connectedForm.afterSubmit)?.(data?.salesCreditPostWithAdjustment as any);
    },
  });

  const [variables, setVariables] = useState<Maybe<ISalesCreditPostWithAdjustmentMutationVariables>>(null);
  useEffect(() => {
    if (variables) {
      salesCreditPostWithAdjustment({
        variables: {
          input: {
            ...variables.input,
            viaTradeCounter: viaTradeCounter,
          },
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variables]);

  const postConfirmationPrompt = usePostConfirmationPrompt(
    postConfirmationState,
    setPostConfirmationState,
    variables,
    setVariables,
    'input.stockAdjustment'
  );

  const customSubmit: (values: IStockAdjustment) => void = (stockAdjustment: IStockAdjustment) => {
    setPostInProgress(true);
    setVariables({
      input: {
        clientMutationId: getClientMutationId(),
        id: salesCredit?.id,
        stockAdjustment: normalizeVariables(stockAdjustment),
      },
    });
  };

  return [customSubmit, postConfirmationPrompt, postInProgress];
};

function responseIsRegularPost(
  response: ISalesCreditPostMutationResult | ISalesCreditPostWithAdjustmentPayload
): response is ISalesCreditPostMutationResult {
  return !!(response as ISalesCreditPostMutationResult)?.salesCreditPost;
}

const SalesCreditPostModal = memo(
  ({ viaTradeCounter, onClose, isOpen }: { viaTradeCounter: boolean; onClose: () => void; isOpen: boolean }) => {
    const connectedForm = useContext(ConnectedFormContext);
    const dispatch = useDispatch();
    const [raiseStockAdjustment] = useMutation<
      IRaiseStockAdjustmentFromSalesCreditMutationResult,
      IRaiseStockAdjustmentFromSalesCreditMutationVariables
    >(RaiseStockAdjustmentFromSalesCredit, {
      onCompleted: (data) => {
        //Unclear why, but if you move this to '.then' on the raiseStockAdjustment call, it doesn't work.
        const stockAdjustmentUUID = data?.raiseStockAdjustmentFromSalesCredit?.result?.uuid;
        dispatch(connectedForm.change('stockAdjustmentUUID', stockAdjustmentUUID));
      },
    });

    const salesCredit: ISalesCredit = connectedForm.values as ISalesCredit;
    const [postButtonDisabled, setPostButtonDisabled] = useState(false);

    const { dispatchToast, dispatchSimpleToast, dismissToast } = useP4ToastController();

    const afterSubmit = (response: ISalesCreditPostMutationResult | ISalesCreditPostWithAdjustmentPayload) => {
      setPostButtonDisabled(false);
      onClose();

      let result;

      if (responseIsRegularPost(response)) {
        result = response.salesCreditPost?.result;
        dispatch(connectedForm.change(false, result));
        dispatchSimpleToast(
          `Sales Credit Posted`,
          `Sales Credit ${result?.documentNumber} was posted successfully`,
          AlertType.Success
        );
      } else if (response?.result?.stockAdjustment) {
        result = response.result;
        dispatch(connectedForm.change(false, result));
      }

      //Must not have any outstanding modals - If there are any, 'afterSubmit()' call will occur when they're closed.
      if (
        !result?.entityMetaData?.actions?.some?.(
          (action: any) =>
            Object.keys(connectedForm.modal || {}).indexOf((action as Maybe<IMetaAction>)?.actionType as string) > -1
        )
      ) {
        connectedForm.afterSubmit?.({ result });
      }

      if (salesCredit?.salesCreditType === 'REFUND') {
        const notificationRefundToastId = `${result?.uuid ?? 'result-missing'}-result`;

        dispatchToast(
          <NotificationRefund
            salesCredit={salesCredit}
            onClose={() => {
              dismissToast(notificationRefundToastId);
            }}
          />,
          {
            title: 'Refund',
            type: AlertType.Action,
            toastId: notificationRefundToastId,
          }
        );
      }
    };

    const [postCreditWithAdjustment, confirmationPrompts, postInProgress] = useSalesCreditPostWithStockAdjustment({
      afterSubmit,
      viaTradeCounter,
    });

    const handleQueryError = useComponentQueryErrorHandling();
    const [postCredit] = useMutation<ISalesCreditPostMutationResult, ISalesCreditPostMutationVariables>(
      SalesCreditPostMutation,
      {
        onCompleted: (data) => {
          setPostButtonDisabled(false);
          afterSubmit?.(data);
        },
        onError: (error) => {
          setPostButtonDisabled(false);
          handleQueryError(error);
        },
      }
    );

    return (
      <>
        <ModalContext.Provider
          value={{
            onClose: () => {
              //Stock adjustment modal is displayed based on the UUID being set.
              //Clear it if the user cancels.
              dispatch(connectedForm.change('stockAdjustmentUUID', null));
            },
          }}
        >
          <FluentModal
            isOpen={!!salesCredit?.stockAdjustmentUUID}
            styles={{
              main: {
                minWidth: '400px',
              },
              scrollableContent: { height: '94vh' },
            }}
          >
            <LazyProductsStockAdjustmentForm
              disabled={postInProgress}
              match={{ params: { uuid: salesCredit?.stockAdjustmentUUID } }}
              temporaryDefault={true}
              modalClose={true}
              customSubmit={postCreditWithAdjustment}
              addCapabilityOnly={true} //Indicates this can only be accessed from an 'adding an adjustment' context (I.e. Treat it as 'Add' for permissions).
              salesCreditAdjustment={false} //Don't allow changing the quantity on the stock adjustment form, should be kept in sync /w credit.
            />
          </FluentModal>
        </ModalContext.Provider>
        <FluentModal
          isOpen={isOpen}
          styles={{
            main: { minWidth: '500px', paddingBottom: '60px' },
          }}
        >
          <BasicHeader>Post Sales Credit</BasicHeader>
          <Wrapper>
            <p>Do you wish to return the products into stock?</p>
            <ModalBottomButtonContainer>
              <Button onClick={onClose}>Cancel</Button>
              <WarningButton
                onClick={() => {
                  setPostButtonDisabled(true);
                  postCredit({
                    variables: {
                      input: {
                        clientMutationId: getClientMutationId(),
                        id: salesCredit.id,
                        viaTradeCounter: !!viaTradeCounter,
                      },
                    },
                  });
                }}
                disabled={postButtonDisabled}
              >
                No, Post Credit
              </WarningButton>
              <InfoButton
                onClick={() => {
                  raiseStockAdjustment(
                    normalizeVariables({
                      variables: {
                        input: {
                          clientMutationId: getClientMutationId(),
                          salesCredit: salesCredit,
                        },
                      },
                    })
                  );
                }}
              >
                Yes, Raise Stock Adjustment
              </InfoButton>
            </ModalBottomButtonContainer>
          </Wrapper>
        </FluentModal>
        {confirmationPrompts}
      </>
    );
  }
);

export default SalesCreditPostModal;
